/**
 * Classificação de SLA. Converte "quanto falta" em token de cor.
 *
 * Fica em shared/dominio e não no componente porque é regra, não pintura:
 * a mesma classificação vale para a lista, para o detalhe e para qualquer
 * fila futura. Componente só escolhe o token.
 */

import type { ChaveDeSla } from '@anery/design-system';

import { minutosAte, type Instante } from './datas';

/** Abaixo disto o caso entra em atenção. Uma hora. */
export const MINUTOS_DE_ATENCAO = 60;

export interface ClassificacaoDeSla {
  readonly chave: ChaveDeSla;
  readonly rotulo: string;
  /** Minutos restantes. `null` quando o caso não tem prazo. */
  readonly minutosRestantes: number | null;
}

const SEM_PRAZO: ClassificacaoDeSla = {
  chave: 'semPrazo',
  rotulo: 'Sem prazo',
  minutosRestantes: null,
};

/**
 * `prazo` nulo é um estado LEGÍTIMO — caso que ainda não tem SLA definido.
 * Não é erro e não é "no prazo": tem token próprio, para ninguém ler
 * ausência de prazo como folga.
 */
export function classificarSla(prazo: Instante | null, referencia: Instante = new Date()): ClassificacaoDeSla {
  if (prazo === null) return SEM_PRAZO;

  const minutosRestantes = minutosAte(prazo, referencia);

  if (minutosRestantes < 0) {
    return { chave: 'estourado', rotulo: 'Estourado', minutosRestantes };
  }
  if (minutosRestantes <= MINUTOS_DE_ATENCAO) {
    return { chave: 'atencao', rotulo: 'Atenção', minutosRestantes };
  }
  return { chave: 'ok', rotulo: 'No prazo', minutosRestantes };
}

/** `2h 15min`, `45min`, `estourado há 30min`. */
export function descreverPrazo(classificacao: ClassificacaoDeSla): string {
  const { minutosRestantes } = classificacao;
  if (minutosRestantes === null) return 'sem prazo definido';

  const absoluto = Math.abs(minutosRestantes);
  const horas = Math.floor(absoluto / 60);
  const minutos = absoluto % 60;
  const duracao = horas > 0 ? `${horas}h ${minutos}min` : `${minutos}min`;

  return minutosRestantes < 0 ? `estourado há ${duracao}` : `faltam ${duracao}`;
}
